import React from "react";
import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import { FileIcon } from "components/FileIcon";
import { CloseIcon } from "components/icons";
import { CircularLoader } from "components/loader";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Tooltip } from "@mui/material";

export default (props: ReactNodeViewProps<HTMLLabelElement>) => {
  const theme = useTheme();
  const { type, src, name, isLoading, errorMessage } = props.node.attrs;
  const hasError = Boolean(errorMessage);

  return (
    <NodeViewWrapper as={"span"} className="file-chip">
      <Tooltip title={errorMessage || ""} disableHoverListener={!hasError}>
        <Stack
          onClick={(e) => {
            e.stopPropagation();
            if (!hasError && src) {
              window.open(src, "_blank", "noopener,noreferrer");
            }
          }}
          direction={"row"}
          sx={{
            p: "0px 8px",
            gap: "8px",
            borderRadius: "16px",
            border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.neutral["010"]}`,
            cursor: hasError ? "default" : "pointer",
            transition: "all 0.2s ease",
            height: "32px",
            width: "fit-content",
            backgroundColor: hasError ? theme.palette.error.light : "transparent",
            alignItems: "center",
            "&:hover": {
              backgroundColor: hasError
                ? theme.palette.error.light
                : theme.palette.neutral["020"],
            },
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            sx={{
              width: "140px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasError ? (
                <ErrorOutlineIcon
                  sx={{ color: theme.palette.error.dark, fontSize: "20px" }}
                />
              ) : (
                <FileIcon type={type} />
              )}
            </Box>

            <Typography
              variant="body-small"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                alignSelf: "center",
                color: hasError ? theme.palette.error.dark : "inherit",
              }}
            >
              {hasError ? errorMessage : name}
            </Typography>
          </Stack>
          {isLoading ? (
            <CircularLoader
              size={18}
              sx={{ color: theme.palette.lightBg.high }}
            />
          ) : (
            <CloseIcon
              onClick={(e) => {
                e.stopPropagation();
                props.editor.commands.deleteSelection();
              }}
              fontSize="small"
            />
          )}
        </Stack>
      </Tooltip>
    </NodeViewWrapper>
  );
};
